import { useState, type FormEvent } from "react"

type WaitersProps = {
  authenticityToken: string
  data: WaitersData
}

type WaitersData = {
  members: readonly Waiter[]
  selected_camp: WaiterCamp | null
}

type WaiterCamp = {
  id: string
  name: string
}

type Waiter = {
  active: boolean
  id: string
  name: string
}

export function Waiters({ authenticityToken, data }: WaitersProps) {
  const [waiters, setWaiters] = useState(data.members)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const selectedCamp = data.selected_camp
  const isSaving = savingId !== null

  async function createWaiter(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!selectedCamp || isSaving) return

    const form = event.currentTarget
    const formData = new FormData(form)
    const name = formData.get("name")?.toString().trim() ?? ""
    if (!name) return

    setError(null)
    setSavingId("new")

    try {
      const createdWaiter = await saveWaiter(
        "/api/waiters",
        {
          accommodation_id: selectedCamp.id,
          active: formData.get("active") === "true",
          name,
        },
        authenticityToken,
      )

      setWaiters((currentWaiters) => sortWaiters([...currentWaiters, createdWaiter]))
      form.reset()
    } catch {
      setError("Waiter could not be saved.")
    } finally {
      setSavingId(null)
    }
  }

  async function updateWaiter(waiter: Waiter, event: FormEvent<HTMLFormElement>) {
    const formData = new FormData(event.currentTarget)
    const name = formData.get("name")?.toString().trim() ?? ""
    if (!name) return

    setError(null)
    setSavingId(waiter.id)

    try {
      const updatedWaiter = await saveWaiter(
        `/api/waiters/${waiter.id}`,
        {
          active: formData.get("active") === "true",
          name,
        },
        authenticityToken,
        "PATCH",
      )

      setWaiters((currentWaiters) =>
        sortWaiters(currentWaiters.map((currentWaiter) =>
          currentWaiter.id === updatedWaiter.id ? updatedWaiter : currentWaiter,
        )),
      )
    } catch {
      setError("Waiter could not be saved.")
    } finally {
      setSavingId(null)
    }
  }

  if (!selectedCamp) {
    return (
      <section className="rounded-[6px] border border-[#d5d5d5] bg-white px-5 py-5">
        <p className="text-sm font-medium leading-5 text-[#817d76]">No Asilia camps are available.</p>
      </section>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="rounded-[6px] border border-[#d5d5d5] bg-white px-5 py-5">
        <h2 className="text-lg font-bold leading-6 text-[#424242]">Add waiter</h2>
        <form className="mt-4 flex flex-wrap items-end gap-3" onSubmit={createWaiter}>
          <div className="min-w-[240px] flex-1">
            <label className="block text-sm font-bold leading-5 text-[#424242]" htmlFor="new_waiter_name">
              Name
            </label>
            <input
              className="mt-1 h-10 w-full rounded border border-[#c7c7c7] bg-white px-3 text-sm font-medium text-[#4a4a4a] outline-none focus:border-[#337ab7] focus:ring-2 focus:ring-[#337ab7]/20"
              disabled={isSaving}
              id="new_waiter_name"
              name="name"
              required
            />
          </div>
          <label className="flex h-10 items-center gap-2 text-sm font-bold leading-5 text-[#424242]">
            <input
              className="h-4 w-4 rounded border-[#c7c7c7] text-[#337ab7]"
              defaultChecked
              disabled={isSaving}
              name="active"
              type="checkbox"
              value="true"
            />
            Active
          </label>
          <button
            className="h-10 rounded bg-[#2b2b2b] px-4 text-sm font-bold leading-5 text-white hover:bg-[#424242] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isSaving}
            type="submit"
          >
            Add waiter
          </button>
        </form>
      </section>

      <section className="rounded-[6px] border border-[#d5d5d5] bg-white px-5 py-5">
        <h2 className="text-lg font-bold leading-6 text-[#424242]">Waiters at {selectedCamp.name}</h2>

        {waiters.length ? (
          <div className="mt-4 flex flex-col divide-y divide-[#e4e4e4]">
            {waiters.map((waiter) => {
              const rowSaving = savingId === waiter.id

              return (
                <article aria-label={`${waiter.name} waiter`} className="py-4" key={waiter.id}>
                  <form
                    className="flex flex-wrap items-end gap-3"
                    onSubmit={(event) => {
                      event.preventDefault()
                      void updateWaiter(waiter, event)
                    }}
                  >
                    <div className="min-w-[240px] flex-1">
                      <label
                        className="block text-sm font-bold leading-5 text-[#424242]"
                        htmlFor={`waiter_${waiter.id}_name`}
                      >
                        Name
                      </label>
                      <input
                        className="mt-1 h-10 w-full rounded border border-[#c7c7c7] bg-white px-3 text-sm font-medium text-[#4a4a4a] outline-none focus:border-[#337ab7] focus:ring-2 focus:ring-[#337ab7]/20"
                        defaultValue={waiter.name}
                        disabled={isSaving}
                        id={`waiter_${waiter.id}_name`}
                        name="name"
                        required
                      />
                    </div>
                    <label className="flex h-10 items-center gap-2 text-sm font-bold leading-5 text-[#424242]">
                      <input
                        className="h-4 w-4 rounded border-[#c7c7c7] text-[#337ab7]"
                        defaultChecked={waiter.active}
                        disabled={isSaving}
                        name="active"
                        type="checkbox"
                        value="true"
                      />
                      Active
                    </label>
                    <button
                      className="h-10 rounded border border-[#d5d5d5] bg-white px-4 text-sm font-bold leading-5 text-[#424242] hover:bg-[#f2f2f2] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={isSaving}
                      type="submit"
                    >
                      {rowSaving ? "Saving" : "Save"}
                    </button>
                  </form>
                </article>
              )
            })}
          </div>
        ) : (
          <p className="mt-4 text-sm font-medium leading-5 text-[#817d76]">
            No waiters have been added for this camp yet.
          </p>
        )}
      </section>

      {error ? (
        <p className="rounded border border-[#e0b4b4] bg-[#fff2f2] px-4 py-3 text-sm font-bold text-[#873230]">
          {error}
        </p>
      ) : null}
    </div>
  )
}

function sortWaiters(waiters: readonly Waiter[]) {
  return [...waiters].sort((firstWaiter, secondWaiter) =>
    firstWaiter.name.localeCompare(secondWaiter.name, undefined, { sensitivity: "base" }) ||
      firstWaiter.id.localeCompare(secondWaiter.id),
  )
}

async function saveWaiter(
  path: string,
  attributes: Record<string, boolean | string>,
  authenticityToken: string,
  method = "POST",
) {
  const response = await fetch(path, {
    body: JSON.stringify({ accommodation_waiter: attributes }),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticityToken,
    },
    method,
  })

  if (!response.ok) {
    throw new Error(`Waiter save failed with ${response.status}`)
  }

  return (await response.json()) as Waiter
}
